import React, { useState, createContext, useContext } from "react";
import { UserInterface } from "../interfaces/UserInterface";
import { setEncryptSessionStorage, getEncryptSessionStorage } from "../utils/EncryptStorage";
import i18nn from "i18next";

//import jwt_decode from "jwt-decode";

export const ResponseStatus = {
  SUCCESS: "SUCCESS",
  FAIL: "FAIL",
  RESET_PASSWORD: "RESET_PASSWORD",
  TWO_FA: "2FA",
  TWO_FA_ERROR: "2FA_ERROR",
  TWO_FA_LIMIT_REACHED: "2FA_LIMIT_REACHED",
  TWO_FA_CODE_EXPIRED: "2FA_CODE_EXPIRED",
  BAD_CREDENTIALS: "BAD_CREDENTIALS",
};


const AuthContext = createContext({
  signOut: () => {},
  signIn: (username, password) => {},
  getSignIn: (username, password) => {},
  getSignInResponseStatus: (rawData) => {},
  tokenLogin: (token) => {},
  setLoadingState: (loading) => {},
  verify2FA: (code) => {},
  resend2FA: () => {},
  currentToken: () => {},
  currentUser: () => {},
  getUserProfile: () => {},
  resetPassword: (newPassword, confirmPassword) => {},
  forgotPassword: (username, token) => {},
  forgotPasswordSubmit: (username, code, newPassword) => {},
  refreshSession: () => {},
  checkSessionExpired: () => {},
  loading: false,
  user: {} as UserInterface | null
});


export const currentToken = (): string => {
  const userToken: string = getEncryptSessionStorage("token");
  return (userToken)?userToken:'';
};

const currentUser = (): UserInterface => {
  const json = getEncryptSessionStorage("user");
  const userProfile: UserInterface = json ? JSON.parse(json) : null;
  return userProfile;
};

export const AuthProvider = (props) => {


  //let token : string = "";
  //let user : UserInterface | null = defaultUserInterface;
  const [token, setToken] = useState<string | null>(currentToken());
  const [user, setUser] = useState<UserInterface | null>(currentUser());
  const [loading, setLoading] = useState(false);

  const setLoadingState = (loading: boolean) => {
    setLoading(loading);
  }

  const tokenLogin = (token: string) => {
    setEncryptSessionStorage("token", token);
    refreshSession();
  }


  const getUserProfile = async () => {
    // https://local.pactool.aircanada.com:8443/userprofile

    setLoading(true);
    try {

      const requestOptions = {
        method: "GET",
        responseType: "application/json",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": "Bearer " + currentToken()
        }
      };

      const rawData = await fetch(process.env.REACT_APP_PACTOOL_API_URL + "/userprofile", requestOptions)
        .then((response) => response.json())
        .then((response) => {
          return response;
        });


      // 1000 - login sucessful with token
      if (rawData?.code === 1000) {
        const newUser = {
          username: rawData.data.username,
          company: rawData.data.company,
          givenName:rawData.data.givenName,
          lastName: rawData.data.lastName,
          userFullName:  rawData.data.givenName + " " + rawData.data.lastName,
          email: rawData.data.email,
          status: rawData.data.status,
          telephoneNumber: rawData.data.telephoneNumber,
          title: rawData.data.title,
          authorities: rawData.data.authorities,
          creditingType: rawData.data.creditingType,
          mpcs: rawData.data.mpcs,
          token:  currentToken()
        } as UserInterface;
        setUser(newUser);

        setEncryptSessionStorage("user", JSON.stringify(newUser));
        return { status: ResponseStatus.SUCCESS, user:JSON.stringify(newUser) };
      }

      // for all other error cases
      if (rawData?.code){
        return { status: ResponseStatus.FAIL, code: rawData?.code, error: rawData?.msg, message: "login.block-form.error."+rawData?.code };
      }

    } catch (error: any) {
      setLoading(false);
      console.debug("ERROR: unexpected error occured ", error);
      return {
        status: ResponseStatus.FAIL,
        code: "0000",
        error: "API down",
        message: "all.block-form.error.0000"
      };
    } finally {
      setLoading(false);
    }
  };

  const getSignIn = (username: string, password: string): Promise<any> => {
    let params = {
      password: password,
      userid: username
    };
    const requestOptions = {
        method: "POST",
        responseType: "application/json",
        body: JSON.stringify(params),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        }
      };

      return fetch(
         `${process.env.REACT_APP_PACTOOL_API_URL}/authenticate?lang=${i18nn.language}`,
        requestOptions
      );
  }

  const getSignInResponseStatus = (rawData: any) => {
    switch (rawData?.code) {
      case 3000:
        // 3000 - 2FA required
        setEncryptSessionStorage("token", rawData.data.token);
        // rawData.token is a temporary token
        return { status: ResponseStatus.TWO_FA, code: rawData?.code, token: rawData.data.token, message: rawData?.msg };

      case 3002:
        // 3002 - 2FA limit reached
        return { status: ResponseStatus.TWO_FA_LIMIT_REACHED, code: rawData?.code, message: rawData?.msg };

      case 3001:
        // 3001 - 2FA code error
        return { status: ResponseStatus.TWO_FA_ERROR, code: rawData?.code, message: rawData?.msg };

      case 1000:
        // 1000 - login sucessful with token
        setEncryptSessionStorage("token", rawData.data.token);
        refreshSession();
        return { status: ResponseStatus.SUCCESS, code: rawData?.code, token: rawData.data.token };

      case 1004:
        // 1004 - bad credentials
        refreshSession();
        return { status: ResponseStatus.BAD_CREDENTIALS, code: rawData?.code, token: rawData.data.token, message: "login.block-form.error.1004" };

      case 2000:
        // 2000 - reset password required
        setEncryptSessionStorage("token", rawData.data.token);
        refreshSession();
        return { status: ResponseStatus.RESET_PASSWORD, code: rawData?.code, token: rawData.data.token, message: "login.block-form.error.2000" };

      default:
        if (rawData?.code) {
          return { status: ResponseStatus.FAIL, code: rawData?.code, error: rawData?.msg, message: "login.block-form.error." + rawData?.code };
        }
    }
  }

  const signIn = async (username: string, password: string) => {
    // https://local.pactool.aircanada.com:8443/authenticate

    setLoading(true);
    try {
      const rawData = await getSignIn(username, password)
        .then((response) => response.json())
        .then((response) => {
          console.debug("SignIn Response: ", response);
          return response;
        });

      return getSignInResponseStatus(rawData);

    } catch (error: any) {
      setLoading(false);
      console.debug("ERROR: unexpected error occured ", error);
      return {
        status: ResponseStatus.FAIL,
        code: "0000",
        error: "API down",
        message: "all.block-form.error.0000"
      };
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async (twoFACode: number) => {
    let params = {
      code: twoFACode + ""
    };

    const currToken = currentToken();

    try {
      const requestOptions = {
        method: "POST",
        responseType: "application/json",
        body: JSON.stringify(params),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": "Bearer " + currToken
        }
      };

      const rawData = await fetch(
        process.env.REACT_APP_PACTOOL_API_URL + "/validate-2fa",
        requestOptions
      )
        .then((response) => response.json())
        .then((response) => {
          return response;
        });

      setEncryptSessionStorage("token", currToken);

      switch (rawData?.code) {
        // 1000 - login sucessful with token
        case 1000:
          setEncryptSessionStorage("token", rawData.data.token);
          refreshSession();
          return { status: ResponseStatus.SUCCESS, code: rawData?.code, token: rawData.data.token };
        // 3001 - 2FA code error
        case 3001:
          return { status: ResponseStatus.TWO_FA_ERROR, code: rawData?.code, token: currToken, message: "login.block-form.error.3001" };
        // 3002 - 2FA limit reached
        case 3002:
          return { status: ResponseStatus.TWO_FA_LIMIT_REACHED, code: rawData?.code, token: currToken, message: "login.block-form.error.3002" };
        // 3003 - 2FA code expired
        case 3003:
          return { status: ResponseStatus.TWO_FA_CODE_EXPIRED, code: rawData?.code, message: "login.block-form.error.3003" };
        // 2002 - block form error
        case 2002:
          // user is blocked, token is still valid
          return { status: ResponseStatus.FAIL, code: rawData?.code, token: currToken, message: "login.block-form.error.2002" };
        default:
          // for all other error cases
          return { status: ResponseStatus.FAIL, code: rawData?.code, error: rawData?.msg, token: currToken, message: "login.block-form.error."+rawData?.code };
      }


    } catch (error: any) {
      setLoading(false);
      console.debug("ERROR: unexpected error occured ", error);
      return {
        status: ResponseStatus.FAIL,
        code: "0000",
        error: "API down",
        message: "all.block-form.error.0000"
      };
    }
  }

  const resend2FA = async () => {
    try {
      const requestOptions = {
        method: "GET",
        responseType: "application/json",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": "Bearer " + currentToken()
        }
      };
      const rawData = await fetch(
        `${process.env.REACT_APP_PACTOOL_API_URL}/request-2fa?lang=${i18nn.language}`,
        requestOptions
      )
        .then((response) => response.json())
        .then((response) => {
          return response;
        });
      // 1000 - resend 2FA code sucessful
      if (rawData?.code === 1000) {
        return { status: ResponseStatus.SUCCESS, code: rawData?.code, token: "" };
      }
      // 3002 - 2FA limit reached
      if (rawData?.code === 3002) {
        return { status: ResponseStatus.TWO_FA_LIMIT_REACHED, code: rawData?.code, token: "", message: "login.block-form.error.3002" };
      }
    } catch (error: any) {
      console.debug("ERROR: unexpected error occured ", error);
      return {
        status: ResponseStatus.FAIL,
        code: "0000",
        error: "API down",
        message: "login.block-form.error.3001"
      };
    }
  };

  const signOut = () => {
    if (token){
      setToken(null);
    }
    setToken(null);
    setUser(null);
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
  };

  /**
   * TODO
   * Reset Password
   * @param user
   * @param newPassword
   * @return response - A successful request returns a promise
   */
  const resetPassword = async (newPassword: string, confirmPassword: string) => {
    //https://local.pactool.aircanada.com:8443/reset

    setLoading(true);
    try {
      let params = {
        newPassword: newPassword,
        confirmPassword: confirmPassword
      };

      const requestOptions = {
        method: "POST",
        responseType: "application/json",
        body: JSON.stringify(params),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": "Bearer " + currentToken()
        }
      };

      const rawData = await fetch(
        process.env.REACT_APP_PACTOOL_API_URL + "/reset",
        requestOptions
      )
        .then((response) => response.json())
        .then((response) => {
          return response;
        });

      // 1000 - login sucessful with token
      if (rawData?.code === 1000) {
        setEncryptSessionStorage("token", rawData.data.token);

        refreshSession();

        return {status:"SUCCESS", code: rawData?.code, token:rawData.token};
      }

      // for all other error cases
      if (rawData?.code){
        return { status: "FAIL", code: rawData?.code, error: rawData?.msg, message: "resetPassword.block-form.error."+rawData?.code };
      }

    } catch (error: any) {
      setLoading(false);
      console.debug("ERROR: unexpected error occured ", error);
      return {
        status: "FAIL",
        code: "0000",
        error: "API down",
        message: "all.block-form.error.0000"
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * WIP
   * Send user a link to reset the password
   * @param username
   * @return response - A successful request returns a promise
   */
  const forgotPassword = async (username: string, token:string) => {
    let response;
    setLoading(true);
    try {
      let params = {
        "g-recaptcha-response": token,
        "userId": username // HHA6115
      };

      const requestOptions = {
        method: "POST",
        responseType: "application/json",
        body: JSON.stringify(params),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      };

      const rawData = await fetch(
        `${process.env.REACT_APP_PACTOOL_API_URL}/request-reset?lang=${i18nn.language}`,
        requestOptions
      )
        .then((response) => response.json())
        .then((response) => {
          return response;
        });

      // 1000 - sucessful with token
      if (rawData?.code === 1000) {
        return {status:"SUCCESS", code: rawData?.code, token:rawData.token};
      }

      // for all other error cases
      if (rawData?.code){
        return { status: "FAIL", code: rawData?.code, error: rawData?.msg, message: "forgotPassword.block-form."+rawData?.code };
      }


    } catch (error: any) {
      setLoading(false);
      console.debug("ERROR: unexpected error occured ", error);
      response = {
        error: error,
        status: "FAIL"
      };
    } finally {
      setLoading(false);
    }
    return response;
  };

  /**
   * Submit the verification code along with the user's new password
   * @param username
   * @param code - MFA code from cognito
   * @param new_password
   * @return response - A promise on success
   */
  const forgotPasswordSubmit = async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    let response;
    setLoading(true);
    try {
      //let data = await Auth.forgotPasswordSubmit(username, code, newPassword);
      response = {
        data: 'data',
        status: "SUCCESS"
      };
    } catch (error: any) {
      console.debug("ERROR: occured submiting the verification code ", error);
      response = {
        error: error,
        status: "FAIL"
      };
    }
    setLoading(false);
    return response;
  };

  /**
   * TODO
   * Refresh Session
   */
  const refreshSession = async () => {
    try {
      // TODO
      const timeout = Number(process.env.REACT_APP_TIMEOUT);
      var currentDate = new Date();
      var expireTime = new Date(currentDate.getTime() + (timeout * 60 * 1000));
      setEncryptSessionStorage("expiry", String(expireTime.getTime()));
      console.info(">>>>> refreshSession " + expireTime.getTime());
      // NEW EXPIRY TIME
    } catch (e) {
      console.debug("ERROR: unable to refresh token", e);
    }
  };

  /**
   * TODO
   * Check Session Expired
   */
  const checkSessionExpired = async () => {

    try {
      let currentTime = Math.floor(Date.now());
      let expiryTime = Number(getEncryptSessionStorage("expiry")) || null;
      console.info(">>>>> checkSessionExpired ");
      console.info(">>>>> currentTime " + currentTime);
      console.info(">>>>> expiryTime " + expiryTime);
      if (expiryTime && currentTime > expiryTime) {
        //logout
        console.info(">>>>> SESSSION EXPIRED!!!!!!!! ");
        setEncryptSessionStorage("sessionExpired", "true");
        signOut();
      } else {
        //refresh
        refreshSession();
      }
    } catch (e) {
      console.debug("ERROR: checkSessionExpired", e);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        signIn,
        getSignIn,
        getSignInResponseStatus,
        setLoadingState,
        tokenLogin,
        signOut,
        verify2FA,
        resend2FA,
        currentToken,
        currentUser,
        getUserProfile,
        resetPassword,
        forgotPassword,
        forgotPasswordSubmit,
        refreshSession,
        checkSessionExpired,
        loading,
        user
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
